<script setup>

import useScavenge from "./composables/scavenger.js";
const { closeModal, currentRoute, modalIsOpen, isLoading } = useScavenge()

</script>

<template>
	<div class="scavenger-modal-holder" v-if="modalIsOpen">
		<div class="scavenger-modal-backdrop"></div>
		<div class="scavenger-modal">
			<div class="scavenger-modal-close" @click.prevent="closeModal">
				<svg width="68px" height="68px" viewBox="0 0 68 68">
					<circle stroke="#000000" stroke-width="9" fill="#FFF800" cx="34" cy="34" r="29.5"></circle>
					<circle fill="#000000" cx="34.1005817" cy="34.1005817" r="19.8873782"></circle>
					<path d="M41.2842712,28.9099026 L35.9809704,34.2132034 L41.2842712,39.5165043 C41.7704072,40.0026402 41.7704072,40.7981353 41.2842712,41.2842712 C40.7981353,41.7704072 40.0026402,41.7704072 39.5165043,41.2842712 L34.2132034,35.9809704 L28.9099026,41.2842712 C28.4237667,41.7704072 27.6282715,41.7704072 27.1421356,41.2842712 C26.6559997,40.7981353 26.6559997,40.0026402 27.1421356,39.5165043 L32.4454365,34.2132034 L27.1421356,28.9099026 C26.6559997,28.4237667 26.6559997,27.6282715 27.1421356,27.1421356 C27.6282715,26.6559997 28.4237667,26.6559997 28.9099026,27.1421356 L34.2132034,32.4454365 L39.5165043,27.1421356 C40.0026402,26.6559997 40.7981353,26.6559997 41.2842712,27.1421356 C41.7704072,27.6282715 41.7704072,28.4237667 41.2842712,28.9099026 Z" fill="#FFFFFF" fill-rule="nonzero"></path>
				</svg>
			</div>
			<div class="borders">
				<span class="brush top"></span>
				<span class="brush right"></span>
				<span class="brush bottom"></span>
				<span class="brush left"></span>
			</div>
			<component :class="{'is-loading': isLoading}" :is="currentRoute" v-if="currentRoute" />
			<div class="scavenger-modal-loader" v-if="isLoading">
				<svg class="spinner-container" viewBox="0 0 44 44">
					<circle class="path" cx="22" cy="22" r="20" fill="none" stroke-width="4"></circle>
				</svg>
			</div>
		</div>
	</div>
</template>

<style lang="scss">

@font-face {
	font-family: 'Afrihost Decalk';
	src: url('https://www.afrihost.com/fibre/assets/fonts/subset-AfrihostDecalk-Bold.woff2') format('woff2'),
	url('https://www.afrihost.com/fibre/assets/fonts/subset-AfrihostDecalk-Bold.woff') format('woff');
	font-weight: normal;
	font-style: normal;
	font-display: swap;
}

@mixin position($position: relative, $coordinates: null null null null) {

	@if type-of($position) == list {
		$coordinates: $position;
		$position: relative;
	}

	$coordinates: unpack($coordinates);

	$offsets: (
		top: nth($coordinates, 1),
		right: nth($coordinates, 2),
		bottom: nth($coordinates, 3),
		left: nth($coordinates, 4)
	);

	position: $position;

	@each $offset, $value in $offsets {
		@if is-length($value) {
			#{$offset}: $value;
		}
	}
}

// Convert shorthand to the 4-value syntax
@function unpack($shorthand) {
	@if length($shorthand) == 1 {
		@return nth($shorthand, 1) nth($shorthand, 1) nth($shorthand, 1) nth($shorthand, 1);
	} @else if length($shorthand) == 2 {
		@return nth($shorthand, 1) nth($shorthand, 2) nth($shorthand, 1) nth($shorthand, 2);
	} @else if length($shorthand) == 3 {
		@return nth($shorthand, 1) nth($shorthand, 2) nth($shorthand, 3) nth($shorthand, 2);
	} @else {
		@return $shorthand;
	}
}

.scavenger-modal-holder {
	position: fixed;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	z-index: 100;
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 1rem 3rem;

	.scavenger-modal-backdrop {
		position: fixed;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		z-index: 1;
		background: rgba(#000, .8);
	}

	.scavenger-modal {
		position: relative;
		z-index: 2;
		width: 74rem;
		background: linear-gradient(to bottom, #F3554F, #E42C28);
		max-width: 100%;
	}

	.scavenger-modal-close {
		display: block;
		position: absolute;
		top: -3rem;
		right: -3rem;
		z-index: 3;
		cursor: pointer;

		svg {
			display: block;
		}
	}

	.scavenger-modal-body {
		position: relative;
		z-index: 2;
		padding: 6rem 5rem;

		color: #fff;
		text-align: center;

		&.is-loading {
			opacity: 0;
		}

		.modal-heading {
			background: #FFF800;
			border: 1.1rem solid #000000;
			border-radius: .4rem;
			font-size: 4rem;
			padding: .8rem 5rem 0;
			line-height: 5rem;
			font-weight: bold;
			font-style: italic;
			font-family: 'Afrihost Decalk', sans-serif;
			color: #000000;
			text-align: center;
			text-shadow: -2px 2px 0 #E42B27;
			min-height: 5rem;
			z-index: 3;

			//@include position(absolute, 0 null null 50%);
			position: absolute;
			top: 0;
			left: 50%;
			transform: translateY(-55%) translateX(-50%);
		}

		p.normal {
			font-size: 1.4rem;
			font-family: 'Afrihost Sans', sans-serif;
			font-weight: 500;
			margin: 0 auto 3rem;
		}

		> p:not(.normal) {
			font-family: 'Afrihost Decalk', sans-serif;
			font-size: 2.5rem;
			line-height: 3rem;
			max-width: 60.5rem;
			margin: 0 auto 3rem;
		}

		.yellow {
			color: #FFF800;
		}
	}

	.borders {
		@include position(absolute, -.5rem);

		z-index: 1;

		&:before {
			content: '';
			position: absolute;
			left: -20px;
			top: -20px;
			width: 100%;
			height: 100%;
			background: url("https://spiderman.afrihost.com/images/test/shadow-lt-purple.svg") left top no-repeat;
			z-index: -1;
		}

		&:after {
			content: '';
			position: absolute;
			right: -20px;
			bottom: -50px;
			width: 100%;
			height: 100%;
			background: url("https://spiderman.afrihost.com/images/test/shadow-rb-blue.svg") right bottom no-repeat;
			z-index: -1;
		}

		.brush {
			position: absolute;
		}

		.brush.top,
		.brush.bottom {
			left: 0;
			right: 0;
			height: 0;
			border-image: url("https://spiderman.afrihost.com/images/test/brush-horizontal.svg") 25 30 10 20 repeat stretch;
		}

		.brush.left,
		.brush.right {
			top: 3px;
			bottom: 3px;
			width: 0;
			border-image: url("https://spiderman.afrihost.com/images/test/brush-vertical.svg") 25 30 10 20 stretch repeat;
		}

		.brush.top { border-top: 29px solid transparent; top: -5px; }
		.brush.bottom { border-top: 29px solid transparent; bottom: -5px; }
		.brush.left { border-left: 26px solid transparent; left: -5px; }
		.brush.right { border-right: 26px solid transparent; right: -5px; }
	}

	.scb {
		border: none;
		appearance: none;
		background: #24135F;
		border-radius: 3rem;
		height: 6rem;
		line-height: 6rem;
		color: #fff;
		font-size: 1.2rem;
		font-weight: bold;
		width: 28rem;
		max-width: 100%;
		padding: 0 5rem;

		&.right {
			margin-left: auto;
		}
	}

	.scavenger-modal-loader {
		@include position(absolute, 1rem);
		//background: rgba(#000, .7);
		display: flex;
		justify-content: center;
		align-items: center;
		//z-index: 1;

		svg {
			-webkit-animation: rotate 2s linear infinite;
			animation: rotate 2s linear infinite;
			z-index: 2;
			width:65px; height:65px;
			margin: 100px auto;
			display:block;
		}

		svg .path {
			stroke-dasharray: 1,150; /* 1%, 101% circumference */
			stroke-dashoffset: 0;
			stroke: rgba(#fff, 0.7);
			stroke-linecap: round;
			-webkit-animation: dash 1.5s ease-in-out infinite;
			animation: dash 1.5s ease-in-out infinite;
		}

		@keyframes rotate {
			100% { transform: rotate(360deg); }
		}

		@keyframes dash {
			0% {
				stroke-dasharray: 1,150;  /* 1%, 101% circumference */
				stroke-dashoffset: 0;
			}
			50% {
				stroke-dasharray: 90,150; /* 70%, 101% circumference */
				stroke-dashoffset: -35;   /* 25% circumference */
			}
			100% {
				stroke-dasharray: 90,150; /* 70%, 101% circumference */
				stroke-dashoffset: -124;  /* -99% circumference */
			}
		}
	}

	.error {
		display: flex;
		justify-content: center;
		align-items: center;
		min-height: 3.5rem;
		background: #FFF800;
		border-radius: 1.8rem;
		margin-bottom: 3rem;

		svg {
			margin-right: 1rem;
		}

		p {
			font-size: 1.1rem;
			font-family: 'Afrihost Sans', sans-serif;
			font-weight: 500;
			color: #DA291C;
			margin: 0;
		}
	}

	.spm-input {
		display: flex;
		width: 100%;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 2rem;

		//+ .scb {
		//	margin-top: 3rem;
		//	margin-left: auto;
		//}

		input {
			appearance: none;
			width: 100%;
			background: rgba(218,41,28,0.60);
			border: .4rem solid #000000;
			border-radius: .8rem;
			height: 6rem;
			padding: 0 2rem;

			font-family: 'Afrihost Decalk', sans-serif;
			font-size: 2.4rem;
			line-height: 6rem;
			//font-style: italic;
			//font-weight: bold;
			color: #000;
		}

		::-webkit-input-placeholder {
			color: #000;
		}

		::-moz-placeholder {
			color: #000;
		}

		:-ms-input-placeholder {
			color: #000;
		}

		:-moz-placeholder {
			color: #000;
		}

		.label {
			font-family: 'Afrihost Decalk', sans-serif;
			font-size: 2.5rem;
			//line-height: 5rem;
		}

		&:has(.label) {
			input {
				max-width: calc(100% - 25rem);
			}
		}

		@media all and (max-width: 600px) {
			&:has(.label) {
				flex-direction: column;
				align-items: flex-start;
				text-align: left;

				.label {
					width: 100%;
					margin-bottom: 1rem;
				}

				input {
					max-width: 100%;
				}
			}
		}
	}
}

</style>
